import React, { useContext, useEffect, useState } from "react";
import Panel from "./Panel";
import {
  Button,
  Dimmer,
  Divider,
  Grid,
  GridColumn,
  Loader,
  Message,
  Segment,
  Header,
  Label,
} from "semantic-ui-react";
import { Link, useParams, useLocation } from "react-router-dom";
import {
  searchOrders,
  lookupAppointment,
  lookupAdditionalDetail,
} from "../libs/requests/orderSearch";
import { authContext } from "../containers/AuthProvider";
import moment from "moment-timezone";
import ZenUpdatesTable from "./ZenUpdatesTable";
import GiganetUpdatesTable from "./GiganetUpdatesTable";
import { getDiagnostics } from "../libs/requests/getDiagnostics";
import { searchServices } from "../libs/requests/serviceSearch";

const dateFn = (val) => moment(val).format("DD-MM-YYYY");

const fields = [
  { key: "customerReference", label: "Customer Reference" },
  { key: "provisionType", label: "Provision Type" },
  { key: "phoneNumber", label: "CLI" },
  { key: "orderedBy", label: "Ordered By" },
  { key: "linkedOrderReference", label: "Linked Order Reference" },
  { key: "rid", label: "Reseller ID" },
  { divider: true },
  { key: "productDescription", label: "Product" },
  { key: "careLevel", label: "Care Level" },
  { key: "trafficWeighting", label: "Traffic Weighting" },
  { key: "preferredActivationDate", label: "Activation Date", format: dateFn },
  { key: "network", label: "Network" },
  { key: "upstreamSpeed", label: "Upstream Speed" },
  { divider: true },
  { key: "organisationName", label: "Organisation" },
  { key: "subPremiseName", label: "Sub Premise" },
  { key: "premiseName", label: "Premise" },
  { key: "thoroughfareNumber", label: "House Number" },
  { key: "thoroughfare", label: "Street" },
  { key: "dependentThoroughfare", label: "Dependent Thoroughfare" },
  { key: "postTown", label: "Town" },
  { key: "postCode", label: "Post Code" },
  { key: "router", label: "Router" },
  { key: "deliveryOptions", label: "Delivery Options" },
  { key: "pricingOption", label: "Pricing" },
  { key: "bespokePrice", label: "Bespoke Price" },
  { key: "isBureauReseller", label: "Is Bureau Reseller" },
  { key: "bureauEndCustomer", label: "Bureau End Customer" },
];

const progressFields = [
  { key: "status", label: "Status" },
  { key: "statusReason", label: "Status Reason" },
  { key: "serviceId", label: "Service ID" },
  { key: "accessLineId", label: "Access Line ID" },
  { key: "accessLineType", label: "Access Line Type" },
  { divider: true },
  { key: "orderDate", label: "Placed Date", format: dateFn },
  { key: "committedDate", label: "Committed Date", format: dateFn },
  { key: "completedDate", label: "Completed Date", format: dateFn },
];

const appointmentFields = [
  { divider: true },
  { key: "appointmentReference", label: "Appointment Reference" },
  { key: "contactFullName", label: "Contact Name" },
  { key: "contactPhoneNumber", label: "Contact Phone Number" },
  { key: "siteSafetyNotes", label: "Site Safety Notes" },
  {
    key: "siteSpecialArrangementNotes",
    label: "Site Special Arrangement Notes",
  },
  { key: "siteVisitNotes", label: "Site Visit Notes" },
];

export const GridItem = ({ field, value, columns }) => {
  const { divider, label } = field;
  columns = columns || 2;

  if (divider || (value !== null && value !== undefined && value !== "")) {
    const displayValue = field.format && value ? field.format(value) : value;

    if (divider) {
      return (
        <GridColumn width={16}>
          <Divider />
        </GridColumn>
      );
    }

    return (
      <GridColumn key={label}>
        <Grid columns={2}>
          <GridColumn>
            <strong>{label}</strong>
          </GridColumn>
          <GridColumn>{displayValue}</GridColumn>
        </Grid>
      </GridColumn>
    );
  }

  return null;
};

const sectionHasData = (sectionFields, order) => {
  return sectionFields.some((field) => {
    const value = order[field.key];
    return value !== null && value !== undefined && value !== "";
  });
};

// Function to modify the "Ordered By" field
const modifyOrderFields = (order) => {
  if (!order) return order;
  if (order.orderedBy && order.orderedBy.toLowerCase() === "ws-api-faelix") {
    return { ...order, orderedBy: "ws-api-hightide" };
  }
  return order;
};

export default function ViewOrder() {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [order, setOrder] = useState({});
  const [zenUpdates, setZenUpdates] = useState([]);
  const [giganetUpdates, setGiganetUpdates] = useState([]);
  const [broadbandConnection, setBroadbandConnection] = useState({});

  const [requestPairs, setRequestPairs] = useState(["BroadbandConnection"]);
  const { reference } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fulfilmentState = queryParams.get("fulfilmentState");
  const orderDate = queryParams.get("orderDate");
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [isAppointmentLoading, setIsAppointmentLoading] = useState(false);
  const [additionalDetails, setAdditionalDetails] = useState({
    contractTerm: null,
    pricingOption: null,
    bespokePrice: null,
    router: null,
    deliveryOptions: null,
    isBureauReseller: null,
    bureauEndCustomer: null,
  });
  const [additionalDetailsLoading, setAdditionalDetailsLoading] =
    useState(false);
  const [serviceStatus, setServiceStatus] = useState(null);

  const { permissions } = useContext(authContext);

  useEffect(() => {
    (async () => {
      try {
        if (permissions?.servicesAccess) {
          const response = await getDiagnostics(reference, requestPairs);
          setBroadbandConnection(response?.broadbandConnection || {});
        }
      } catch (err) {
        console.error(err);
      }
    })();
  }, [reference, permissions, requestPairs]);

  const fetchAppointmentDetails = async (appointmentReference) => {
    if (!appointmentReference) return;

    try {
      setIsAppointmentLoading(true);
      const appointment = await lookupAppointment(appointmentReference);
      setAppointmentDetails(appointment);
    } catch (err) {
      console.error("Failed to fetch appointment details:", err);
    } finally {
      setIsAppointmentLoading(false);
    }
  };

  const fetchAdditionalDetails = async (zenReference) => {
    if (!zenReference) return;

    try {
      setAdditionalDetailsLoading(true);
      const additionalDetail = await lookupAdditionalDetail(zenReference);
      setAdditionalDetails(additionalDetail);
    } catch (err) {
      console.error("Failed to fetch additional details:", err);
    } finally {
      setAdditionalDetailsLoading(false);
    }
  };

  const fetchServiceStatus = async (zenReference) => {
    if (!zenReference) return;
    try {
      const result = await searchServices(zenReference, 0, 1);
      const svc = result?.services?.[0];
      if (svc?.serviceStatus) {
        setServiceStatus(svc.serviceStatus);
      }
    } catch (err) {
      console.error("Failed to fetch service status:", err);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (permissions?.orderAccess) {
          setIsLoading(true);
          // Use fulfilmentState=0 (all states) — the URL param is the list filter, not the order's state.
          const result = await searchOrders(reference, 0);

          const foundOrder = result.orders.find(
            (order) =>
              order.reference === reference &&
              (!orderDate || order.orderDate === orderDate)
          );

          if (!foundOrder && result.orders.length > 0) {
            setOrder(modifyOrderFields(result.orders[0]));
          } else {
            setOrder(modifyOrderFields(foundOrder));
          }

          setZenUpdates(result.zenUpdates);
          setGiganetUpdates(result.giganetUpdates);
          setIsLoading(false);

          if (foundOrder?.appointmentReference) {
            fetchAppointmentDetails(foundOrder.appointmentReference);
          }

          if (foundOrder?.zenReference) {
            fetchAdditionalDetails(foundOrder.zenReference);
          }

          fetchServiceStatus(reference);
        }
      } catch (err) {
        console.error(err);
        setIsError(true);
        setIsLoading(false);
      }
    })();
  }, [reference, orderDate, permissions]);

  useEffect(() => {}, [zenUpdates]);

  useEffect(() => {}, [giganetUpdates]);

  const hasAppointment = () => {
    if (!order) return false;
    return !!(
      order.requiresEngineerAppointment === "Yes" || order.contactFullName
    );
  };

  if (!isLoading && !permissions.orderAccess) {
    return (
      <Segment padded>
        <Message error header="You don't have permission to view this page" />
      </Segment>
    );
  }

  const effectiveStatus = serviceStatus === "ActiveCeasePending"
    ? "Active - Cease Pending"
    : order?.status;

  const header = (
    <Header
      as="h2"
      textAlign="center"
      style={{
        margin: "20px 0",
        color: "black",
        zIndex: 1000,
        backgroundColor: "rgba(255, 255, 255, 0.8)",
        padding: "10px",
        borderRadius: "5px",
      }}
    >
      Order Reference {reference}
      <Header.Subheader style={{ color: "black" }}>
        {order ? effectiveStatus : ""}
      </Header.Subheader>
    </Header>
  );

  let extraFields = hasAppointment()
    ? progressFields.concat(appointmentFields)
    : progressFields;

  return (
    <>
      <Segment padded>
        <Dimmer active={isLoading} inverted>
          <Loader inverted>Loading</Loader>
        </Dimmer>
        <div style={{ marginBottom: "20px" }}>
          <Panel header={header}>
            {isError && (
              <Message
                error
                header="There was an error trying to view this order, please try again later"
              />
            )}
            {!order && !isError && (
              <Message info header="The order could not be found" />
            )}
            {!!order && sectionHasData(fields, order) && (
              <Grid columns={3}>
                {fields.map((field) => (
                  <GridItem
                    field={field}
                    value={order[field.key]}
                    key={field.key}
                  />
                ))}
              </Grid>
            )}
          </Panel>
        </div>
        {!!order && sectionHasData(extraFields, order) && (
          <Panel header={"Order Progress"}>
            <Grid columns={3}>
              {extraFields.map((field) => (
                <GridItem
                  field={field}
                  value={field.key === "status" ? effectiveStatus : order[field.key]}
                  key={field.key}
                />
              ))}
              {serviceStatus === "ActiveCeasePending" && (
                <GridItem
                  field={{ key: "serviceStatus", label: "Service Status" }}
                  value="Active - Cease Pending"
                />
              )}
            </Grid>
          </Panel>
        )}

        {broadbandConnection &&
          (broadbandConnection.username ||
            broadbandConnection.password ||
            broadbandConnection.routerIpAddress) &&
          !permissions.isL2 && (
            <Panel header={"Connection Details"}>
              <Grid columns={3}>
                <Label>Username: {broadbandConnection?.username ?? ""}</Label>
                <Label>Password: {broadbandConnection?.password ?? ""}</Label>
                <Label>
                  Router IP Address:{" "}
                  {broadbandConnection?.routerIpAddress ?? ""}
                </Label>
              </Grid>
            </Panel>
          )}

        {appointmentDetails && (
          <Panel header={"Appointment Details"}>
            <Grid columns={3}>
              <GridItem
                field={{
                  key: "date",
                  label: "Appointment Date",
                  format: dateFn,
                }}
                value={appointmentDetails.appointmentSlot?.date}
              />
              <GridItem
                field={{ key: "timeslot", label: "Timeslot" }}
                value={appointmentDetails.appointmentSlot?.timeslot}
              />
              <GridItem
                field={{ key: "appointmentType", label: "Appointment Type" }}
                value={appointmentDetails.appointmentSlot?.appointmentType}
              />
            </Grid>
          </Panel>
        )}

        {additionalDetails &&
          (additionalDetails.contractTerm ||
            additionalDetails.pricingOption ||
            (additionalDetails.isBureauReseller &&
              permissions?.isNimansTenant) ||
            additionalDetails.bureauEndCustomer ||
            additionalDetails.bespokePrice ||
            additionalDetails.router ||
            additionalDetails.deliveryOptions) && (
            <Panel header="Additional Details">
              <Grid columns={2}>
                {additionalDetails.contractTerm && (
                  <GridItem
                    field={{ key: "contractTerm", label: "Contract Term" }}
                    value={additionalDetails.contractTerm}
                  />
                )}
                {additionalDetails.pricingOption && (
                  <GridItem
                    field={{ key: "pricingOption", label: "Pricing Option" }}
                    value={additionalDetails.pricingOption}
                  />
                )}
                {additionalDetails.isBureauReseller &&
                  permissions?.isNimansTenant && (
                    <GridItem
                      field={{
                        key: "isBureauReseller",
                        label: "Bureau Reseller",
                      }}
                      value="Yes"
                    />
                  )}
                {additionalDetails.bureauEndCustomer && (
                  <GridItem
                    field={{
                      key: "bureauEndCustomer",
                      label: "Bureau End Customer",
                    }}
                    value={additionalDetails.bureauEndCustomer}
                  />
                )}
                {additionalDetails.bespokePrice && (
                  <GridItem
                    field={{ key: "bespokePrice", label: "Bespoke Price" }}
                    value={additionalDetails.bespokePrice}
                  />
                )}
                {additionalDetails.router && (
                  <GridItem
                    field={{ key: "router", label: "Router" }}
                    value={additionalDetails.router}
                  />
                )}
                {additionalDetails.deliveryOptions && (
                  <GridItem
                    field={{
                      key: "deliveryOptions",
                      label: "Delivery Options",
                    }}
                    value={additionalDetails.deliveryOptions}
                  />
                )}
              </Grid>
            </Panel>
          )}

        {Object.keys(zenUpdates).length > 0 && (
          <Panel header={"Order Updates"}>
            <ZenUpdatesTable updates={zenUpdates} />
          </Panel>
        )}

        {Object.keys(giganetUpdates).length > 0 && (
          <Panel header={"Order Updates"}>
            <GiganetUpdatesTable updates={giganetUpdates} />
          </Panel>
        )}
      </Segment>
      <Link to="/SearchOrders">
        <Button
          primary
          className="button-primary"
          style={{ marginTop: "20px" }}
        >
          Back
        </Button>
      </Link>
    </>
  );
}
